import React, { useCallback, useEffect, useState } from 'react';
import { SidebarCategories } from './sidebar/SidebarCategories';
import { SidebarSubcategories } from './sidebar/SidebarSubcategories';
import { SidebarUnitsPanel } from './sidebar/SidebarUnitsPanel';
import { SidebarMaterialsPanel } from './sidebar/SidebarMaterialsPanel';
import { MaterialsCatalogPanel } from './sidebar/MaterialsCatalogPanel';
import { SidebarModelsPanel } from './sidebar/SidebarModelsPanel';
import { ModelsCatalogPanel } from './sidebar/ModelsCatalogPanel';
interface EnhancedSidebarProps {
  onUnitSelect: (unit: any) => void;
  showProperties?: boolean;
  onPropertiesClose?: () => void;
}
export function EnhancedSidebar({
  onUnitSelect,
  showProperties = false,
  onPropertiesClose
}: EnhancedSidebarProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('carpentry');
  const [activeSubcategory, setActiveSubcategory] = useState('floor-cabinets');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [customWidths, setCustomWidths] = useState<Record<string, number>>({});
  const [recentUnits, setRecentUnits] = useState<any[]>([]);
  const [showSubcategories, setShowSubcategories] = useState(true);
  const [selectedUnit, setSelectedUnit] = useState<any>(null);
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const [categoriesCollapsed, setCategoriesCollapsed] = useState(false);
  // Materials state
  const [showCabinetMaterials, setShowCabinetMaterials] = useState(false);
  const [cabinetMaterialSearch, setCabinetMaterialSearch] = useState('');
  const [cabinetMaterialFavorites, setCabinetMaterialFavorites] = useState<string[]>([]);
  const [showOnlyCabinetFavorites, setShowOnlyCabinetFavorites] = useState(false);
  const [showOnlySelectedMaterials, setShowOnlySelectedMaterials] = useState(false);
  // Models state
  const [showModels, setShowModels] = useState(false);
  const [modelSearch, setModelSearch] = useState('');
  const [modelFavorites, setModelFavorites] = useState<string[]>([]);
  const [showOnlyModelFavorites, setShowOnlyModelFavorites] = useState(false);
  const [showOnlySelectedModels, setShowOnlySelectedModels] = useState(false);
  // Cabinet material options
  const cabinetMaterials = [{
    id: 'oak-casella',
    name: 'HT384 ST40 White Casella Oak',
    category: 'Wood',
    property: 'Oak',
    image: 'https://images.unsplash.com/photo-1566041510639-8d95a2490bfb?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=100&ixid=MnwxfDB8MXxyYW5kb218MHx8d29vZHx8fHx8fHwxNjg0ODg3MjA1&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=100',
    color: 'bg-red-900',
    brand: 'EGGER'
  }, {
    id: 'light-transparent',
    name: 'Light transparent',
    category: 'Glass',
    property: 'Transparent',
    image: 'https://images.unsplash.com/photo-1557683316-973673baf926?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=100&ixid=MnwxfDB8MXxyYW5kb218MHx8Z2xhc3N8fHx8fHx8MTY4NDg4NzMyNw&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=100',
    color: 'bg-gray-100'
  }, {
    id: 'white-ceramic',
    name: 'Bright matte white ceramic',
    category: 'Tile',
    property: 'Ceramic',
    secondaryProperty: 'White',
    image: 'https://images.unsplash.com/photo-1532323544230-7191fd51bc1b?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=100&ixid=MnwxfDB8MXxyYW5kb218MHx8Y2VyYW1pY3x8fHx8fHwxNjg0ODg3MzU0&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=100',
    color: 'bg-gray-100',
    isFavorite: true
  }, {
    id: 'brushed-metal',
    name: 'Brushed metallic finish',
    category: 'Metal',
    property: 'Brushed',
    secondaryProperty: 'Cool',
    image: 'https://images.unsplash.com/photo-1629991273932-7f5e2a7a9c7e?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=100&ixid=MnwxfDB8MXxyYW5kb218MHx8bWV0YWx8fHx8fHwxNjg0ODg3Mjkx&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=100',
    color: 'bg-gray-300',
    isFavorite: true
  }, {
    id: 'white-marble',
    name: 'Polished white marble with',
    category: 'Stone',
    property: 'Natural',
    image: 'https://images.unsplash.com/photo-1588345921523-c2dcdb7f1dcd?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=100&ixid=MnwxfDB8MXxyYW5kb218MHx8bWFyYmxlfHx8fHx8MTY4NDg4NzMyNw&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=100',
    color: 'bg-gray-100'
  }, {
    id: 'olive-fabric',
    name: 'Olive green fabric with a',
    category: 'Fabric',
    property: 'Soft',
    image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=100&ixid=MnwxfDB8MXxyYW5kb218MHx8ZmFicmljfHx8fHx8MTY4NDg4NzMzNQ&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=100',
    color: 'bg-olive-500'
  }, {
    id: 'grey-concrete',
    name: 'Smooth grey concrete',
    category: 'Stone',
    property: 'Industrial',
    image: 'https://images.unsplash.com/photo-1515364229692-4e584c18e4f7?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=100&ixid=MnwxfDB8MXxyYW5kb218MHx8Y29uY3JldGV8fHx8fHwxNjg0ODg3MzU0&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=100',
    color: 'bg-gray-400'
  }, {
    id: 'blue-polycarbonate',
    name: 'Light blue polycarbonate',
    category: 'Plastic',
    property: 'Transparent',
    image: 'https://images.unsplash.com/photo-1618354691792-d1d42acfd860?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=100&ixid=MnwxfDB8MXxyYW5kb218MHx8cGxhc3RpY3x8fHx8fDE2ODQ4ODczNzU&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=100',
    color: 'bg-blue-200'
  }];
  // Kitchen model options
  const kitchenModels = [{
    id: 'modern-minimalist',
    name: 'Modern Minimalist',
    category: 'Modern',
    style: 'Handleless',
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=100&ixid=MnwxfDB8MXxyYW5kb218MHx8bW9kZXJuJTIwa2l0Y2hlbnx8fHx8fDE2ODQ4ODcyMDU&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=100',
    brand: 'NORDIK',
    isFavorite: true
  }, {
    id: 'scandinavian-light',
    name: 'Scandinavian Light',
    category: 'Scandinavian',
    style: 'Minimalist',
    image: 'https://images.unsplash.com/photo-1574739782594-db4ead022697?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=100&ixid=MnwxfDB8MXxyYW5kb218MHx8c2NhbmRpbmF2aWFuJTIwa2l0Y2hlbnx8fHx8fDE2ODQ4ODczMjc&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=100',
    feature: 'Open Shelving'
  }, {
    id: 'classic-shaker',
    name: 'Classic Shaker Style',
    category: 'Classic',
    style: 'Shaker',
    image: 'https://images.unsplash.com/photo-1556909212-d5b604d0c90d?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=100&ixid=MnwxfDB8MXxyYW5kb218MHx8Y2xhc3NpYyUyMGtpdGNoZW58fHx8fHwxNjg0ODg3MzU0&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=100',
    brand: 'CLASSIC',
    isFavorite: true
  }, {
    id: 'industrial-loft',
    name: 'Industrial Loft',
    category: 'Industrial',
    style: 'Open',
    image: 'https://images.unsplash.com/photo-1556912173-3bb406ef7e8d?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=100&ixid=MnwxfDB8MXxyYW5kb218MHx8aW5kdXN0cmlhbCUyMGtpdGNoZW58fHx8fHwxNjg0ODg3Mjkx&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=100',
    feature: 'Metal Accents'
  }, {
    id: 'rustic-farmhouse',
    name: 'Rustic Farmhouse',
    category: 'Rustic',
    style: 'Traditional',
    image: 'https://images.unsplash.com/photo-1484154218962-a197022b5858?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=100&ixid=MnwxfDB8MXxyYW5kb218MHx8cnVzdGljJTIwa2l0Y2hlbnx8fHx8fDE2ODQ4ODczMjc&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=100',
    feature: 'Open Shelving'
  }, {
    id: 'contemporary-gloss',
    name: 'Contemporary High Gloss',
    category: 'Modern',
    style: 'Handleless',
    image: 'https://images.unsplash.com/photo-1600585152220-90363fe7e115?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=100&ixid=MnwxfDB8MXxyYW5kb218MHx8bW9kZXJuJTIwa2l0Y2hlbnx8fHx8fDE2ODQ4ODczMzU&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=100',
    brand: 'LUMINA'
  }, {
    id: 'transitional-blend',
    name: 'Transitional Blend',
    category: 'Transitional',
    style: 'Mixed',
    image: 'https://images.unsplash.com/photo-1556911220-bff31c812dba?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=100&ixid=MnwxfDB8MXxyYW5kb218MHx8a2l0Y2hlbnx8fHx8fDE2ODQ4ODczNTQ&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=100',
    feature: 'Island'
  }, {
    id: 'minimalist-handleless',
    name: 'Minimalist Handleless',
    category: 'Minimalist',
    style: 'Integrated',
    image: 'https://images.unsplash.com/photo-1600566752355-35792bedcfea?crop=entropy&cs=tinysrgb&fit=crop&fm=jpg&h=100&ixid=MnwxfDB8MXxyYW5kb218MHx8bWluaW1hbGlzdCUyMGtpdGNoZW58fHx8fHwxNjg0ODg3Mzc1&ixlib=rb-4.0.3&q=80&utm_campaign=api-credit&utm_medium=referral&utm_source=unsplash_source&w=100',
    brand: 'PURE'
  }];
  // Initialize cabinet material favorites from materials with isFavorite property
  useEffect(() => {
    const initialFavorites = cabinetMaterials.filter(material => material.isFavorite).map(material => material.id);
    if (initialFavorites.length > 0) {
      setCabinetMaterialFavorites(initialFavorites);
    }
    // Initialize model favorites from models with isFavorite property
    const initialModelFavorites = kitchenModels.filter(model => model.isFavorite).map(model => model.id);
    if (initialModelFavorites.length > 0) {
      setModelFavorites(initialModelFavorites);
    }
  }, []);
  // Define the categories
  const categories = [{
    id: 'carpentry',
    name: 'Carpentry',
    icon: 'BoxIcon',
    isActive: true
  }, {
    id: 'appliances',
    name: 'Appliances',
    icon: 'RefrigeratorIcon',
    isActive: false
  }, {
    id: 'styling',
    name: 'Accessories',
    icon: 'SofaIcon',
    isActive: false
  }, {
    id: 'fronts',
    name: 'Fronts',
    icon: 'SquareIcon',
    isActive: false,
    hasDivider: true
  }, {
    id: 'materials',
    name: 'Materials',
    icon: 'PaintBucketIcon',
    isActive: false
  }];
  // Define the subcategories for each category
  const subcategories = {
    carpentry: [{
      id: 'floor-cabinets',
      name: 'Floor Cabinets',
      icon: 'ChevronRightIcon'
    }, {
      id: 'upper-cabinets',
      name: 'Upper Cabinets',
      icon: 'ChevronRightIcon'
    }, {
      id: 'pantry',
      name: 'Pantry',
      icon: 'ChevronRightIcon'
    }, {
      id: 'tall-oven-cabinet',
      name: 'Tall Oven Cabinet',
      icon: 'ChevronRightIcon'
    }, {
      id: 'counters',
      name: 'Counters',
      icon: 'ChevronRightIcon'
    }, {
      id: 'spacial-elements',
      name: 'Special Elements',
      icon: 'ChevronRightIcon'
    }, {
      id: 'ready-islands',
      name: 'Ready Island',
      icon: 'ChevronRightIcon'
    }],
    appliances: [{
      id: 'refrigerators',
      name: 'Refrigerators',
      icon: 'ChevronRightIcon'
    }, {
      id: 'ovens',
      name: 'Ovens',
      icon: 'ChevronRightIcon'
    }, {
      id: 'dishwashers',
      name: 'Dishwashers',
      icon: 'ChevronRightIcon'
    }],
    materials: [{
      id: 'colors',
      name: 'Colors',
      icon: 'ChevronRightIcon'
    }, {
      id: 'textures',
      name: 'Textures',
      icon: 'ChevronRightIcon'
    }, {
      id: 'handles',
      name: 'Handles',
      icon: 'ChevronRightIcon'
    }],
    fronts: [{
      id: 'cabinets',
      name: 'Cabinets',
      icon: 'ChevronRightIcon'
    }, {
      id: 'floor',
      name: 'Floor',
      icon: 'ChevronRightIcon'
    }, {
      id: 'walls',
      name: 'Walls',
      icon: 'ChevronRightIcon'
    }, {
      id: 'windows',
      name: 'Windows',
      icon: 'ChevronRightIcon'
    }, {
      id: 'doors',
      name: 'Doors',
      icon: 'ChevronRightIcon'
    }, {
      id: 'appliances',
      name: 'Appliances',
      icon: 'ChevronRightIcon'
    }],
    properties: []
  };
  // Define filter options for each subcategory
  const filterOptions = {
    'floor-cabinets': [{
      id: '1-front',
      name: '1 Front',
      group: 'type'
    }, {
      id: '2-fronts',
      name: '2 Fronts',
      group: 'type'
    }, {
      id: '3-fronts',
      name: '3 Fronts',
      group: 'type'
    }, {
      id: '4-fronts',
      name: '4 Fronts',
      group: 'type'
    }, {
      id: '5-fronts',
      name: '5 Fronts',
      group: 'type'
    }, {
      id: 'corner-units',
      name: 'Corner Units',
      group: 'type'
    }, {
      id: 'open-shelves',
      name: 'Open Shelves',
      group: 'feature'
    }, {
      id: 'glass-front',
      name: 'Glass-front',
      group: 'feature'
    }],
    'upper-cabinets': [{
      id: 'doors',
      name: 'Doors',
      group: 'type'
    }, {
      id: 'lift-up-doors',
      name: 'Lift-Up Doors',
      group: 'type'
    }, {
      id: 'double-lift-up-doors',
      name: 'Double Lift-Up Doors',
      group: 'type'
    }, {
      id: 'corner-units',
      name: 'Corner Units',
      group: 'type'
    }, {
      id: 'open-shelves',
      name: 'Open Shelves',
      group: 'type'
    }, {
      id: 'glass-front',
      name: 'Glass-front',
      group: 'feature'
    }],
    'tall-oven-cabinet': [{
      id: 'double',
      name: 'Double',
      group: 'type'
    }, {
      id: 'compact',
      name: 'Compact',
      group: 'type'
    }, {
      id: 'standard',
      name: 'Standard',
      group: 'type'
    }, {
      id: 'tall',
      name: 'Tall',
      group: 'type'
    }],
    pantry: [{
      id: 'width-up-to-60',
      name: 'Width up to 60 cm',
      group: 'type'
    }, {
      id: 'width-over-60',
      name: 'Width over 60 cm',
      group: 'type'
    }, {
      id: 'open-shelves',
      name: 'Open Shelves',
      group: 'type'
    }, {
      id: 'corner-units',
      name: 'Corner Units',
      group: 'type'
    }, {
      id: 'glass-front',
      name: 'Glass-front',
      group: 'feature'
    }],
    counters: [{
      id: 'standard',
      name: 'Standard',
      group: 'type'
    }, {
      id: 'corner',
      name: 'Corner',
      group: 'type'
    }, {
      id: 'custom',
      name: 'Custom',
      group: 'type'
    }],
    'spacial-elements': [{
      id: 'fillers',
      name: 'Fillers',
      group: 'type'
    }, {
      id: 'corner-solutions',
      name: 'Corner Solutions',
      group: 'type'
    }, {
      id: 'decorative',
      name: 'Decorative',
      group: 'type'
    }],
    'ready-islands': [{
      id: 'small',
      name: 'Small',
      group: 'type'
    }, {
      id: 'medium',
      name: 'Medium',
      group: 'type'
    }, {
      id: 'large',
      name: 'Large',
      group: 'type'
    }, {
      id: 'with-seating',
      name: 'With Seating',
      group: 'feature'
    }]
  };
  // Cabinet units for each subcategory
  const cabinetUnits = {
    'floor-cabinets': [{
      id: 'cabinet-324',
      name: 'Cabinet 324',
      width: 80,
      height: 85,
      depth: 60,
      fronts: 1,
      toeKickHeight: 10,
      wallOffset: 0,
      note: '',
      type: '1-front',
      features: [],
      image: 'https://images.unsplash.com/photo-1556911220-bff31c812dba?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'cabinet-382',
      name: 'Cabinet 382',
      width: 80,
      height: 85,
      depth: 60,
      fronts: 2,
      toeKickHeight: 10,
      wallOffset: 0,
      note: '',
      type: '2-fronts',
      features: [],
      image: 'https://images.unsplash.com/photo-1574621100236-d25b64cfd647?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'cabinet-363',
      name: 'Cabinet 363',
      width: 50,
      height: 85,
      depth: 60,
      fronts: 3,
      toeKickHeight: 10,
      wallOffset: 0,
      note: '',
      type: '3-fronts',
      features: [],
      image: 'https://images.unsplash.com/photo-1560184897-ae75f418493e?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'cabinet-371',
      name: 'Cabinet 371',
      width: 80,
      height: 85,
      depth: 60,
      fronts: 4,
      toeKickHeight: 10,
      wallOffset: 0,
      note: '',
      type: '4-fronts',
      features: [],
      image: 'https://images.unsplash.com/photo-1556909212-d5b604d0c90d?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'cabinet-377',
      name: 'Cabinet 377',
      width: 70,
      height: 85,
      depth: 60,
      fronts: 5,
      toeKickHeight: 10,
      wallOffset: 0,
      note: '',
      type: '5-fronts',
      features: [],
      image: 'https://images.unsplash.com/photo-1570222094114-d054a817e56b?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'cabinet-corner',
      name: 'Corner Cabinet',
      width: 90,
      height: 85,
      depth: 90,
      fronts: 'corner',
      toeKickHeight: 10,
      wallOffset: 0,
      note: '',
      type: 'corner-units',
      features: [],
      image: 'https://images.unsplash.com/photo-1570222094114-d054a817e56b?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'cabinet-open',
      name: 'Open Cabinet',
      width: 60,
      height: 85,
      depth: 60,
      fronts: 0,
      toeKickHeight: 10,
      wallOffset: 0,
      note: '',
      type: '1-front',
      features: ['open-shelves'],
      image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'cabinet-glass',
      name: 'Glass Cabinet',
      width: 60,
      height: 85,
      depth: 60,
      fronts: 2,
      toeKickHeight: 10,
      wallOffset: 0,
      note: '',
      type: '2-fronts',
      features: ['glass-front'],
      image: 'https://images.unsplash.com/photo-1556910638-8c95987bcc5e?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }],
    'upper-cabinets': [{
      id: 'upper-standard-1',
      name: 'Upper Cabinet 401',
      width: 40,
      height: 70,
      depth: 35,
      fronts: 1,
      toeKickHeight: 0,
      wallOffset: 0,
      note: '',
      type: 'doors',
      features: [],
      image: 'https://images.unsplash.com/photo-1556909212-d5b604d0c90d?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'upper-standard-2',
      name: 'Upper Cabinet 402',
      width: 80,
      height: 70,
      depth: 35,
      fronts: 2,
      toeKickHeight: 0,
      wallOffset: 0,
      note: '',
      type: 'doors',
      features: [],
      image: 'https://images.unsplash.com/photo-1556910638-8c95987bcc5e?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'upper-lift-up',
      name: 'Lift-Up Cabinet',
      width: 60,
      height: 40,
      depth: 35,
      fronts: 1,
      toeKickHeight: 0,
      wallOffset: 0,
      note: '',
      type: 'lift-up-doors',
      features: [],
      image: 'https://images.unsplash.com/photo-1556909212-d5b604d0c90d?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'upper-double-lift',
      name: 'Double Lift-Up Cabinet',
      width: 80,
      height: 40,
      depth: 35,
      fronts: 2,
      toeKickHeight: 0,
      wallOffset: 0,
      note: '',
      type: 'double-lift-up-doors',
      features: [],
      image: 'https://images.unsplash.com/photo-1556910638-8c95987bcc5e?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'upper-corner',
      name: 'Upper Corner Cabinet',
      width: 60,
      height: 70,
      depth: 35,
      fronts: 'corner',
      toeKickHeight: 0,
      wallOffset: 0,
      note: '',
      type: 'corner-units',
      features: [],
      image: 'https://images.unsplash.com/photo-1556910638-8c95987bcc5e?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'upper-open',
      name: 'Open Shelving',
      width: 60,
      height: 70,
      depth: 35,
      fronts: 'open',
      toeKickHeight: 0,
      wallOffset: 0,
      note: '',
      type: 'doors',
      features: ['open-shelves'],
      image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'upper-glass',
      name: 'Glass Door Cabinet',
      width: 60,
      height: 70,
      depth: 35,
      fronts: 1,
      toeKickHeight: 0,
      wallOffset: 0,
      note: '',
      type: 'doors',
      features: ['glass-front'],
      image: 'https://images.unsplash.com/photo-1556910638-8c95987bcc5e?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }],
    pantry: [{
      id: 'pantry-small',
      name: 'Small Pantry',
      width: 60,
      height: 200,
      depth: 60,
      fronts: 2,
      toeKickHeight: 10,
      wallOffset: 0,
      note: '',
      type: 'width-up-to-60',
      features: [],
      image: 'https://images.unsplash.com/photo-1556912998-c57cc6b63cd7?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'pantry-large',
      name: 'Large Pantry',
      width: 80,
      height: 200,
      depth: 60,
      fronts: 2,
      toeKickHeight: 10,
      wallOffset: 0,
      note: '',
      type: 'width-over-60',
      features: [],
      image: 'https://images.unsplash.com/photo-1556912998-c57cc6b63cd7?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'pantry-corner',
      name: 'Corner Pantry',
      width: 100,
      height: 200,
      depth: 100,
      fronts: 'corner',
      toeKickHeight: 10,
      wallOffset: 0,
      note: '',
      type: 'corner-units',
      features: [],
      image: 'https://images.unsplash.com/photo-1556912998-c57cc6b63cd7?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'pantry-open',
      name: 'Open Pantry',
      width: 60,
      height: 200,
      depth: 60,
      fronts: 'open',
      toeKickHeight: 10,
      wallOffset: 0,
      note: '',
      type: 'open-shelves',
      features: [],
      image: 'https://images.unsplash.com/photo-1556912998-c57cc6b63cd7?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'pantry-glass',
      name: 'Glass Pantry',
      width: 60,
      height: 200,
      depth: 60,
      fronts: 2,
      toeKickHeight: 10,
      wallOffset: 0,
      note: '',
      type: 'width-up-to-60',
      features: ['glass-front'],
      image: 'https://images.unsplash.com/photo-1556912998-c57cc6b63cd7?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }],
    'tall-oven-cabinet': [{
      id: 'oven-cabinet-double',
      name: 'Double Oven Cabinet',
      width: 60,
      height: 200,
      depth: 60,
      fronts: 2,
      toeKickHeight: 10,
      wallOffset: 0,
      note: '',
      type: 'double',
      features: [],
      image: 'https://images.unsplash.com/photo-1590794056226-79ef3a8147e1?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'oven-cabinet-compact',
      name: 'Compact Oven Cabinet',
      width: 60,
      height: 140,
      depth: 60,
      fronts: 1,
      toeKickHeight: 10,
      wallOffset: 0,
      note: '',
      type: 'compact',
      features: [],
      image: 'https://images.unsplash.com/photo-1590794056226-79ef3a8147e1?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'oven-cabinet-standard',
      name: 'Standard Oven Cabinet',
      width: 60,
      height: 200,
      depth: 60,
      fronts: 1,
      toeKickHeight: 10,
      wallOffset: 0,
      note: '',
      type: 'standard',
      features: [],
      image: 'https://images.unsplash.com/photo-1590794056226-79ef3a8147e1?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'oven-cabinet-tall',
      name: 'Tall Oven Cabinet',
      width: 60,
      height: 220,
      depth: 60,
      fronts: 1,
      toeKickHeight: 10,
      wallOffset: 0,
      note: '',
      type: 'tall',
      features: [],
      image: 'https://images.unsplash.com/photo-1590794056226-79ef3a8147e1?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }],
    counters: [{
      id: 'counter-standard',
      name: 'Standard Counter',
      width: 120,
      height: 4,
      depth: 60,
      fronts: 0,
      toeKickHeight: 0,
      wallOffset: 0,
      note: '',
      type: 'standard',
      features: [],
      image: 'https://images.unsplash.com/photo-1588345921523-c2dcdb7f1dcd?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'counter-corner',
      name: 'Corner Counter',
      width: 90,
      height: 4,
      depth: 90,
      fronts: 0,
      toeKickHeight: 0,
      wallOffset: 0,
      note: '',
      type: 'corner',
      features: [],
      image: 'https://images.unsplash.com/photo-1588345921523-c2dcdb7f1dcd?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }],
    'spacial-elements': [{
      id: 'corner-solution',
      name: 'Corner Solution',
      width: 90,
      height: 85,
      depth: 90,
      fronts: 'corner',
      toeKickHeight: 10,
      wallOffset: 0,
      note: '',
      type: 'corner-solutions',
      features: [],
      image: 'https://images.unsplash.com/photo-1570222094114-d054a817e56b?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'filler-element',
      name: 'Filler Element',
      width: 10,
      height: 85,
      depth: 60,
      fronts: 0,
      toeKickHeight: 10,
      wallOffset: 0,
      note: '',
      type: 'fillers',
      features: [],
      image: 'https://images.unsplash.com/photo-1570222094114-d054a817e56b?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }],
    'ready-islands': [{
      id: 'island-standard',
      name: 'Standard Island',
      width: 180,
      height: 85,
      depth: 90,
      fronts: 4,
      toeKickHeight: 10,
      wallOffset: 0,
      note: '',
      type: 'medium',
      features: [],
      image: 'https://images.unsplash.com/photo-1565538810643-b5bdb714032a?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'island-with-seating',
      name: 'Island with Seating',
      width: 240,
      height: 85,
      depth: 120,
      fronts: 4,
      toeKickHeight: 10,
      wallOffset: 0,
      note: '',
      type: 'large',
      features: ['with-seating'],
      image: 'https://images.unsplash.com/photo-1565538810643-b5bdb714032a?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }],
    // Material subcategories
    colors: [{
      id: 'white',
      name: 'White',
      color: '#FFFFFF',
      image: 'https://images.unsplash.com/photo-1588345921523-c2dcdb7f1dcd?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'black',
      name: 'Black',
      color: '#000000',
      image: 'https://images.unsplash.com/photo-1550684376-efcbd6e3f031?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'gray',
      name: 'Gray',
      color: '#808080',
      image: 'https://images.unsplash.com/photo-1589939705384-5185137a7f0f?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'blue',
      name: 'Blue',
      color: '#0000FF',
      image: 'https://images.unsplash.com/photo-1557683316-973673baf926?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }],
    textures: [{
      id: 'wood',
      name: 'Wood',
      image: 'https://images.unsplash.com/photo-1566041510639-8d95a2490bfb?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'marble',
      name: 'Marble',
      image: 'https://images.unsplash.com/photo-1588345921523-c2dcdb7f1dcd?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'concrete',
      name: 'Concrete',
      image: 'https://images.unsplash.com/photo-1515364229692-4e584c18e4f7?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }],
    handles: [{
      id: 'modern',
      name: 'Modern Handle',
      image: 'https://images.unsplash.com/photo-1556910638-8c95987bcc5e?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }, {
      id: 'classic',
      name: 'Classic Handle',
      image: 'https://images.unsplash.com/photo-1556911220-bff31c812dba?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
    }],
    // Add empty arrays for other subcategories to prevent errors
    refrigerators: [],
    ovens: [],
    dishwashers: []
  };
  // Memoize the handleCabinetsMaterialClick function with useCallback
  const handleCabinetsMaterialClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowCabinetMaterials(prev => !prev);
  }, []);
  // Memoize the handleModelsClick function with useCallback
  const handleModelsClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowModels(prev => !prev);
  }, []);
  const toggleFavorite = (unitId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (favorites.includes(unitId)) {
      setFavorites(favorites.filter(id => id !== unitId));
    } else {
      setFavorites([...favorites, unitId]);
    }
  };
  const toggleCabinetMaterialFavorite = (materialId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (cabinetMaterialFavorites.includes(materialId)) {
      setCabinetMaterialFavorites(cabinetMaterialFavorites.filter(id => id !== materialId));
    } else {
      setCabinetMaterialFavorites([...cabinetMaterialFavorites, materialId]);
    }
  };
  const toggleModelFavorite = (modelId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (modelFavorites.includes(modelId)) {
      setModelFavorites(modelFavorites.filter(id => id !== modelId));
    } else {
      setModelFavorites([...modelFavorites, modelId]);
    }
  };
  const adjustUnitWidth = (unitId: string, adjustment: number, event: React.MouseEvent) => {
    event.stopPropagation();
    const unit = findUnitById(unitId);
    if (!unit) return;
    const currentWidth = customWidths[unitId] || unit.width;
    const newWidth = Math.max(30, currentWidth + adjustment); // Minimum width of 30cm
    setCustomWidths({
      ...customWidths,
      [unitId]: newWidth
    });
  };
  const setCustomWidth = (unitId: string, width: number, event: React.MouseEvent) => {
    event.stopPropagation();
    if (width < 30) width = 30; // Minimum width of 30cm
    if (width > 300) width = 300; // Maximum width of 300cm
    setCustomWidths({
      ...customWidths,
      [unitId]: width
    });
  };
  const findUnitById = (unitId: string) => {
    const allUnits = Object.values(cabinetUnits).flat();
    return allUnits.find(unit => unit.id === unitId);
  };
  const handleUnitSelect = unit => {
    // Use custom width if set
    const width = customWidths[unit.id] || unit.width;
    // Create a copy of the unit with updated width
    const selectedUnit = {
      ...unit,
      width: width,
      category: activeCategory,
      subcategory: activeSubcategory,
      rotationX: 0,
      rotationY: 0,
      rotationZ: 0
    };
    // Set the selected unit for properties panel
    setSelectedUnit(selectedUnit);
    // Add to recent units if not already there
    if (!recentUnits.some(u => u.id === unit.id)) {
      const updatedRecentUnits = [unit, ...recentUnits].slice(0, 6); // Keep only the most recent 6
      setRecentUnits(updatedRecentUnits);
    }
    onUnitSelect(selectedUnit);
  };
  const handleModelSelect = model => {
    console.log('Selected model:', model);
    // Here you would implement the logic to apply the model
    setShowModels(false);
  };
  // Toggle a filter
  const toggleFilter = (filterId: string) => {
    if (activeFilters.includes(filterId)) {
      setActiveFilters(activeFilters.filter(id => id !== filterId));
    } else {
      setActiveFilters([...activeFilters, filterId]);
    }
  };
  // Filter units based on search query, active filters, and favorites
  const filterUnits = units => {
    if (!units) return [];
    return units.filter(unit => {
      // Match search query
      const matchesSearch = unit.name.toLowerCase().includes(searchQuery.toLowerCase());
      // Match active filters
      let matchesFilters = true;
      if (activeFilters.length > 0) {
        // Check if unit type matches any of the active type filters
        const typeFilters = activeFilters.filter(f => {
          const option = filterOptions[activeSubcategory]?.find(o => o.id === f);
          return option && option.group === 'type';
        });
        // Check if unit features match any of the active feature filters
        const featureFilters = activeFilters.filter(f => {
          const option = filterOptions[activeSubcategory]?.find(o => o.id === f);
          return option && option.group === 'feature';
        });
        // If we have type filters, the unit must match at least one
        if (typeFilters.length > 0) {
          const matchesType = typeFilters.includes(unit.type);
          if (!matchesType) return false;
        }
        // If we have feature filters, the unit must have all these features
        if (featureFilters.length > 0) {
          const hasAllFeatures = featureFilters.every(f => unit.features && unit.features.includes(f));
          if (!hasAllFeatures) return false;
        }
      }
      // Match favorites filter
      const matchesFavorites = !showOnlyFavorites || favorites.includes(unit.id);
      return matchesSearch && matchesFilters && matchesFavorites;
    });
  };
  // Filter cabinet materials based on search query and favorites
  const filterCabinetMaterials = () => {
    return cabinetMaterials.filter(material => {
      // Match search query
      const matchesSearch = material.name.toLowerCase().includes(cabinetMaterialSearch.toLowerCase());
      // Match favorites filter
      const matchesFavorites = !showOnlyCabinetFavorites || cabinetMaterialFavorites.includes(material.id);
      return matchesSearch && matchesFavorites;
    });
  };
  // Filter kitchen models based on search query and favorites
  const filterKitchenModels = () => {
    return kitchenModels.filter(model => {
      // Match search query
      const matchesSearch = model.name.toLowerCase().includes(modelSearch.toLowerCase());
      // Match favorites filter
      const matchesFavorites = !showOnlyModelFavorites || modelFavorites.includes(model.id);
      return matchesSearch && matchesFavorites;
    });
  };
  // Get the display name for the current subcategory
  const getCurrentSubcategoryName = () => {
    const subcat = subcategories[activeCategory]?.find(sc => sc.id === activeSubcategory);
    return subcat ? subcat.name : '';
  };
  // Get the display name for the current category
  const getCurrentCategoryName = () => {
    const cat = categories.find(c => c.id === activeCategory);
    return cat ? cat.name : '';
  };
  // When changing the active category, set the first subcategory as active
  useEffect(() => {
    if (subcategories[activeCategory]?.length > 0) {
      setActiveSubcategory(subcategories[activeCategory][0].id);
      setShowSubcategories(true);
    }
  }, [activeCategory]);
  // Reset active filters when changing subcategory
  useEffect(() => {
    setActiveFilters([]);
  }, [activeSubcategory]);
  // Handle category selection - now it just sets the active category
  const handleCategorySelect = categoryId => {
    setActiveCategory(categoryId);
  };
  // Handle subcategory selection
  const handleSubcategorySelect = subcategoryId => {
    setActiveSubcategory(subcategoryId);
    // Close the main menu when a subcategory is selected
    setCategoriesCollapsed(true);
  };
  // Toggle categories column collapse
  const toggleCategoriesCollapse = () => {
    setCategoriesCollapsed(!categoriesCollapsed);
  };
  // Toggle view between All Cabinets and Favorites
  const toggleView = (view: 'all' | 'favorites') => {
    if (view === 'favorites') {
      setShowOnlyFavorites(true);
    } else {
      setShowOnlyFavorites(false);
    }
  };
  // Get current filter options based on active subcategory
  const getCurrentFilterOptions = () => {
    return filterOptions[activeSubcategory] || [];
  };
  // Group filter options by their group property
  const getGroupedFilterOptions = () => {
    const options = getCurrentFilterOptions();
    const typeOptions = options.filter(option => option.group === 'type');
    const featureOptions = options.filter(option => option.group === 'feature');
    return {
      typeOptions,
      featureOptions
    };
  };
  // Handle back button in cabinet materials panel
  const handleCabinetMaterialsBack = () => {
    setShowCabinetMaterials(false);
  };
  // Handle back button in models panel
  const handleModelsBack = () => {
    setShowModels(false);
  };
  // Handle material selection
  const handleMaterialSelect = material => {
    console.log('Selected material:', material);
    // Here you would implement the logic to apply the material
    setShowCabinetMaterials(false);
  };
  return <div className="flex h-full overflow-hidden bg-white dark:bg-darkbg-secondary" data-id="element-157">
      {/* First column: Categories */}
      <SidebarCategories categories={categories} activeCategory={activeCategory} handleCategorySelect={handleCategorySelect} categoriesCollapsed={categoriesCollapsed} data-id="element-158" />
      {/* Second column: Subcategories */}
      <SidebarSubcategories activeCategory={activeCategory} activeSubcategory={activeSubcategory} subcategories={subcategories} getCurrentCategoryName={getCurrentCategoryName} handleSubcategorySelect={handleSubcategorySelect} toggleCategoriesCollapse={toggleCategoriesCollapse} categoriesCollapsed={categoriesCollapsed} showCabinetMaterials={showCabinetMaterials} setCabinetMaterialSearch={setCabinetMaterialSearch} setShowCabinetMaterials={setShowCabinetMaterials} data-id="element-159" />
      {/* Properties panel - updated to match the image */}
      {showProperties && onPropertiesClose && <div className="w-80 border-r border-gray-200 dark:border-darkbg-border overflow-y-auto" data-id="element-160">
          {/* Properties panel content */}
        </div>}
      {/* Cabinet Materials Panel */}
      {showCabinetMaterials && <SidebarMaterialsPanel cabinetMaterials={cabinetMaterials} showCabinetMaterials={showCabinetMaterials} handleCabinetMaterialsBack={handleCabinetMaterialsBack} cabinetMaterialSearch={cabinetMaterialSearch} setCabinetMaterialSearch={setCabinetMaterialSearch} filterCabinetMaterials={filterCabinetMaterials} toggleCabinetMaterialFavorite={toggleCabinetMaterialFavorite} cabinetMaterialFavorites={cabinetMaterialFavorites} handleMaterialSelect={handleMaterialSelect} showOnlyCabinetFavorites={showOnlyCabinetFavorites} setShowOnlyCabinetFavorites={setShowOnlyCabinetFavorites} showOnlySelectedMaterials={showOnlySelectedMaterials} setShowOnlySelectedMaterials={setShowOnlySelectedMaterials} data-id="element-161" />}
      {/* Models Panel */}
      {showModels && <SidebarModelsPanel kitchenModels={kitchenModels} showModels={showModels} handleModelsBack={handleModelsBack} modelSearch={modelSearch} setModelSearch={setModelSearch} filterKitchenModels={filterKitchenModels} toggleModelFavorite={toggleModelFavorite} modelFavorites={modelFavorites} handleModelSelect={handleModelSelect} showOnlyModelFavorites={showOnlyModelFavorites} setShowOnlyModelFavorites={setShowOnlyModelFavorites} showOnlySelectedModels={showOnlySelectedModels} setShowOnlySelectedModels={setShowOnlySelectedModels} data-id="element-162" />}
      {/* Materials Catalog Panel */}
      {activeCategory === 'materials' && !showCabinetMaterials && <MaterialsCatalogPanel cabinetMaterialSearch={cabinetMaterialSearch} setCabinetMaterialSearch={setCabinetMaterialSearch} showOnlyCabinetFavorites={showOnlyCabinetFavorites} setShowOnlyCabinetFavorites={setShowOnlyCabinetFavorites} showOnlySelectedMaterials={showOnlySelectedMaterials} setShowOnlySelectedMaterials={setShowOnlySelectedMaterials} cabinetMaterials={cabinetMaterials} data-id="element-163" />}
      {/* Models Catalog Panel */}
      {activeCategory === 'fronts' && !showModels && <ModelsCatalogPanel modelSearch={modelSearch} setModelSearch={setModelSearch} showOnlyModelFavorites={showOnlyModelFavorites} setShowOnlyModelFavorites={setShowOnlyModelFavorites} showOnlySelectedModels={showOnlySelectedModels} setShowOnlySelectedModels={setShowOnlySelectedModels} kitchenModels={kitchenModels} data-id="element-164" />}
      {/* Units Panel */}
      {!showProperties && !showCabinetMaterials && !showModels && activeCategory !== 'materials' && activeCategory !== 'fronts' && <SidebarUnitsPanel activeCategory={activeCategory} activeSubcategory={activeSubcategory} filterUnits={filterUnits} cabinetUnits={cabinetUnits} handleUnitSelect={handleUnitSelect} toggleFilter={toggleFilter} toggleView={toggleView} showOnlyFavorites={showOnlyFavorites} getGroupedFilterOptions={getGroupedFilterOptions} activeFilters={activeFilters} toggleFavorite={toggleFavorite} favorites={favorites} adjustUnitWidth={adjustUnitWidth} setCustomWidth={setCustomWidth} customWidths={customWidths} data-id="element-165" />}
    </div>;
}